/* PipelineOpsDashboard
 *
 * The Lead's control room for the Genspark/Claw orchestrator. Surfaces:
 *   1. Headline stats — pipeline_total, locked_total, lock_rate, DLQ open, demand pending
 *   2. Per-day series — chains ingested vs chains locked (rolling N days)
 *   3. Era coverage gaps — eras with the lowest lock counts surface first
 *   4. Outbound webhook DLQ — list, retry, discard
 *   5. Reference-demand queue — targets with N inbound cross-references
 *   6. Pipeline API key — fingerprint + rotate + last-rotated-at
 *
 * Backend surface:
 *   GET  /api/admin/pipeline/ops?days=30
 *   GET  /api/admin/pipeline/dlq?include_resolved&include_discarded
 *   POST /api/admin/pipeline/dlq/{id}/retry
 *   POST /api/admin/pipeline/dlq/{id}/discard
 *   GET  /api/admin/pipeline/demand
 *   GET  /api/admin/pipeline/api-key/status
 *   POST /api/admin/pipeline/api-key/rotate
 */
import { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import {
  RefreshCcw, Activity, AlertTriangle, KeyRound, Webhook, Layers,
  RotateCcw, Trash2, CheckCircle2, Copy, Link2, Zap, FileCheck2, ClipboardCopy, Clock,
} from 'lucide-react';

const COPY_FALLBACK = (s) => {
  try {
    const ta = document.createElement('textarea');
    ta.value = s;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return true;
  } catch { return false; }
};

function StatCard({ label, value, accent, testId, sub }) {
  return (
    <div className="glass-panel p-4 flex flex-col gap-1" data-testid={testId}>
      <div className="font-hud text-[10px] tracking-widest text-white/55 uppercase">{label}</div>
      <div className={`font-historical text-2xl md:text-3xl ${accent || 'text-[#D4A84A]'}`}>{value}</div>
      {sub && <div className="font-mono text-[10px] text-white/45">{sub}</div>}
    </div>
  );
}

function MiniBars({ series }) {
  const max = Math.max(1, ...series.map(r => Math.max(r.ingested || 0, r.locked || 0)));
  return (
    <div className="flex items-end gap-1 h-24" data-testid="ops-by-day-series">
      {series.map((row, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${row.date}\n  ingested: ${row.ingested}\n  locked: ${row.locked}`}>
          <div className="flex flex-col items-stretch w-full gap-0.5 h-full justify-end">
            <div className="bg-[#2F8A92]/55 hover:bg-[#2F8A92]" style={{ height: `${((row.ingested || 0) / max) * 100}%` }} />
            <div className="bg-[#D4A84A]/85 hover:bg-[#D4A84A]" style={{ height: `${((row.locked || 0) / max) * 100}%` }} />
          </div>
          <div className="font-mono text-[8px] text-white/40">{row.date.slice(5)}</div>
        </div>
      ))}
    </div>
  );
}

function EraGapsList({ items }) {
  return (
    <div className="space-y-1.5" data-testid="ops-era-coverage-gaps">
      {items.map(row => {
        const ratio = row.total > 0 ? row.locked / row.total : 0;
        const accent = ratio < 0.3 ? 'text-[#FF4444]' : ratio < 0.7 ? 'text-[#D4A84A]' : 'text-emerald-400';
        return (
          <div key={row.era} className="flex items-center justify-between font-hud text-xs tracking-wider px-2 py-1.5 border-l-2 border-[#D4A84A]/30">
            <span className="text-white/85 uppercase">{row.era}</span>
            <span className={`font-mono ${accent}`}>{row.locked}/{row.total || 0} locked</span>
          </div>
        );
      })}
    </div>
  );
}

export default function PipelineOpsDashboard() {
  const [ops, setOps] = useState(null);
  const [dlq, setDlq] = useState({ summary: { open: 0, resolved: 0, discarded: 0, total: 0 }, items: [] });
  const [demand, setDemand] = useState({ threshold: 3, items: [] });
  const [keyStatus, setKeyStatus] = useState(null);
  const [sourceHealth, setSourceHealth] = useState({ summary: { total_tracked: 0, broken: 0, last_run: null }, items: [] });
  const [scanning, setScanning] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [days, setDays] = useState(30);
  const [includeResolved, setIncludeResolved] = useState(false);
  const [includeDiscarded, setIncludeDiscarded] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  // Pre-flight validator panel state
  const [validateInput, setValidateInput] = useState('');
  const [validateResult, setValidateResult] = useState(null);
  const [validating, setValidating] = useState(false);
  const [validateError, setValidateError] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  // Scheduler observability state
  const [recentRuns, setRecentRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [opsR, dlqR, demR, keyR, srcR] = await Promise.all([
        api.get(`/admin/pipeline/ops?days=${days}`),
        api.get(`/admin/pipeline/dlq?include_resolved=${includeResolved}&include_discarded=${includeDiscarded}`),
        api.get('/admin/pipeline/demand'),
        api.get('/admin/pipeline/api-key/status'),
        api.get('/admin/source-health?broken_only=true&limit=50'),
      ]);
      setOps(opsR.data);
      setDlq(dlqR.data);
      setDemand(demR.data);
      setKeyStatus(keyR.data);
      setSourceHealth(srcR.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Load failed.');
    } finally {
      setLoading(false);
    }
  }, [days, includeResolved, includeDiscarded]);

  useEffect(() => { refresh(); }, [refresh]);

  const onRetry = async (id) => {
    setActionMsg(null);
    try {
      const r = await api.post(`/admin/pipeline/dlq/${id}/retry`);
      setActionMsg({ kind: 'ok', text: r.data.already_resolved ? 'Already resolved.' : 'Retry scheduled.' });
      setTimeout(refresh, 1200);
    } catch (e) {
      setActionMsg({ kind: 'err', text: e?.response?.data?.detail || 'Retry failed.' });
    }
  };
  const onDiscard = async (id) => {
    const reason = window.prompt('Reason for discarding this webhook entry? (optional)') || '';
    setActionMsg(null);
    try {
      await api.post(`/admin/pipeline/dlq/${id}/discard`, { reason });
      setActionMsg({ kind: 'ok', text: 'Discarded.' });
      refresh();
    } catch (e) {
      setActionMsg({ kind: 'err', text: e?.response?.data?.detail || 'Discard failed.' });
    }
  };
  const onRotate = async () => {
    if (!window.confirm('Rotate the pipeline API key? The previous key will stop working immediately.')) return;
    setRotating(true);
    setActionMsg(null);
    try {
      const r = await api.post('/admin/pipeline/api-key/rotate', { confirm: true });
      setRevealedKey(r.data.api_key);
      setActionMsg({ kind: 'ok', text: 'Key rotated. Copy it now — it will not be shown again.' });
      refresh();
    } catch (e) {
      setActionMsg({ kind: 'err', text: e?.response?.data?.detail || 'Rotate failed.' });
    } finally {
      setRotating(false);
    }
  };

  const onScanSources = async () => {
    setScanning(true);
    setActionMsg(null);
    try {
      const r = await api.post('/admin/source-health/scan', {});
      const s = r.data.summary || {};
      setActionMsg({
        kind: 'ok',
        text: `Scanned ${s.scanned} URLs across all locked chains in ${s.duration_ms}ms — ${s.broken} broken, ${s.healthy} healthy.`,
      });
      refresh();
    } catch (e) {
      setActionMsg({ kind: 'err', text: e?.response?.data?.detail || 'Scan failed.' });
    } finally {
      setScanning(false);
    }
  };

  const onValidatePayload = async () => {
    setValidateError(null);
    setValidateResult(null);
    let parsed;
    try {
      parsed = JSON.parse(validateInput);
    } catch (e) {
      setValidateError(`JSON parse error: ${e.message}`);
      return;
    }
    setValidating(true);
    try {
      const r = await api.post('/admin/pipeline/validate', parsed);
      setValidateResult(r.data);
    } catch (e) {
      setValidateError(e?.response?.data?.detail || e.message || 'Validate failed.');
    } finally {
      setValidating(false);
    }
  };

  const onLoadTemplate = async () => {
    setLoadingTemplate(true);
    setValidateError(null);
    try {
      const r = await api.get('/admin/pipeline/template');
      // Strip the writer comments — they're noise in the textarea
      const tpl = { ...(r.data?.template || {}) };
      delete tpl._comment_for_writer;
      if (Array.isArray(tpl.cross_references)) {
        tpl.cross_references = tpl.cross_references.map((cr) => {
          const { _comment_for_writer, ...rest } = cr || {};
          return rest;
        });
      }
      if (Array.isArray(tpl.steps)) {
        tpl.steps = tpl.steps.map((s) => {
          if (Array.isArray(s?.sources)) {
            s.sources = s.sources.map((src) => {
              const { _comment, ...rest } = src || {};
              return rest;
            });
          }
          return s;
        });
      }
      setValidateInput(JSON.stringify(tpl, null, 2));
      setValidateResult(null);
    } catch (e) {
      setValidateError(e?.response?.data?.detail || 'Could not load template.');
    } finally {
      setLoadingTemplate(false);
    }
  };

  const loadRecentRuns = async () => {
    setRunsLoading(true);
    try {
      const r = await api.get('/admin/source-health/recent-runs', { params: { limit: 5 } });
      setRecentRuns(r.data?.runs || []);
    } catch {/* keep panel empty on error */}
    setRunsLoading(false);
  };

  useEffect(() => { loadRecentRuns(); }, []);

  const onTriggerSample = async () => {    if (!window.confirm('Insert a fixture chain in DRAFT state and route to the Pipeline Queue so you can walk it through the full review → lock → spine activation flow?')) return;
    setTriggering(true);
    setActionMsg(null);
    try {
      const r = await api.post('/admin/pipeline/trigger-sample');
      const txt = r.data.already_existed
        ? `Fixture already exists (${r.data.chain_status}). chain_id=${r.data.chain_id}`
        : `Fixture chain ingested as DRAFT. chain_id=${r.data.chain_id} — open Pipeline Queue to review/lock.`;
      setActionMsg({ kind: 'ok', text: txt });
      refresh();
    } catch (e) {
      setActionMsg({ kind: 'err', text: e?.response?.data?.detail || 'Trigger failed.' });
    } finally {
      setTriggering(false);
    }
  };

  if (loading && !ops) {
    return (
      <div className="p-6">
        <div className="font-hud text-[#2F8A92] text-sm tracking-widest animate-pulse" data-testid="pipeline-ops-loading">
          LOADING PIPELINE OPS...
        </div>
      </div>
    );
  }
  if (err && !ops) {
    return (
      <div className="p-6">
        <div className="font-hud text-[#FF4444] text-sm tracking-wider" data-testid="pipeline-ops-error">{err}</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="pipeline-ops-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-historical text-[#D4A84A] text-2xl md:text-3xl lg:text-4xl font-light tracking-tight">
            Pipeline Operations
          </h2>
          <p className="font-hud text-[10px] md:text-xs text-white/65 tracking-wider mt-1">
            CHAINS · LOCK RATE · ERA COVERAGE · WEBHOOK DLQ · REFERENCE DEMAND · API KEY
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            data-testid="ops-days-select"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="bg-black/60 border border-[#D4A84A]/30 text-[#D4A84A] font-hud text-xs px-3 py-1.5 focus:outline-none"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
          <button
            data-testid="ops-refresh-btn"
            onClick={refresh}
            className="font-hud text-xs tracking-widest text-white/85 border border-[#2F8A92]/40 px-3 py-1.5 hover:text-[#2F8A92] inline-flex items-center gap-2"
          >
            <RefreshCcw size={12} /> REFRESH
          </button>
        </div>
      </div>

      {actionMsg && (
        <div
          data-testid="ops-action-msg"
          className={`font-mono text-xs tracking-wide px-3 py-2 border-l-2 ${
            actionMsg.kind === 'ok' ? 'text-emerald-300 border-emerald-400' : 'text-red-300 border-red-500'
          }`}
        >
          {actionMsg.text}
        </div>
      )}

      {/* Headline stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard testId="ops-stat-pipeline-total" label="Pipeline Chains" value={ops?.pipeline_total ?? 0}
          sub={`window: ${ops?.window_days ?? days}d`} />
        <StatCard testId="ops-stat-locked-total" label="Locked" value={ops?.locked_total ?? 0}
          accent="text-emerald-300" />
        <StatCard testId="ops-stat-lock-rate" label="Lock Rate"
          value={`${Math.round(((ops?.lock_rate ?? 0) * 100))}%`}
          accent={(ops?.lock_rate ?? 0) >= 0.5 ? 'text-emerald-300' : 'text-[#D4A84A]'} />
        <StatCard testId="ops-stat-dlq-open" label="Webhook DLQ Open" value={ops?.dlq?.open ?? 0}
          accent={(ops?.dlq?.open ?? 0) > 0 ? 'text-[#FF4444]' : 'text-white/85'} />
        <StatCard testId="ops-stat-demand-fired" label="Demand Threshold Fired" value={ops?.demand?.fired ?? 0}
          sub={`pending: ${ops?.demand?.pending ?? 0}`} />
      </div>

      {/* By-day chart */}
      <section className="glass-panel p-4 space-y-3" data-testid="ops-chart-panel">
        <div className="flex items-center gap-2 font-hud text-xs tracking-widest text-[#2F8A92] uppercase">
          <Activity size={14} /> Chains per day
          <span className="ml-auto font-mono text-[10px] text-white/45">cyan = ingested · gold = locked</span>
        </div>
        {ops?.by_day && <MiniBars series={ops.by_day} />}
      </section>

      {/* Era coverage gaps */}
      <section className="glass-panel p-4 space-y-3" data-testid="ops-era-panel">
        <div className="flex items-center gap-2 font-hud text-xs tracking-widest text-[#2F8A92] uppercase">
          <Layers size={14} /> Era coverage gaps
          <span className="ml-auto font-mono text-[10px] text-white/45">worst first</span>
        </div>
        {ops?.era_coverage_gaps && <EraGapsList items={ops.era_coverage_gaps} />}
      </section>

      {/* DLQ */}
      <section className="glass-panel p-4 space-y-3" data-testid="ops-dlq-panel">
        <div className="flex flex-wrap items-center gap-3 font-hud text-xs tracking-widest text-[#2F8A92] uppercase">
          <Webhook size={14} /> Webhook Dead-Letter Queue
          <span className="ml-auto font-mono text-[10px] text-white/55">
            open {dlq.summary.open} · resolved {dlq.summary.resolved} · discarded {dlq.summary.discarded}
          </span>
        </div>
        <div className="flex flex-wrap gap-3 font-hud text-[10px] tracking-wider text-white/65">
          <label className="inline-flex items-center gap-2">
            <input data-testid="dlq-include-resolved" type="checkbox" checked={includeResolved}
              onChange={(e) => setIncludeResolved(e.target.checked)} className="accent-[#D4A84A]" />
            include resolved
          </label>
          <label className="inline-flex items-center gap-2">
            <input data-testid="dlq-include-discarded" type="checkbox" checked={includeDiscarded}
              onChange={(e) => setIncludeDiscarded(e.target.checked)} className="accent-[#D4A84A]" />
            include discarded
          </label>
        </div>
        {dlq.items.length === 0 ? (
          <div data-testid="dlq-empty" className="font-mono text-[11px] text-white/45 italic">No entries match the current filter. The pipeline is healthy.</div>
        ) : (
          <div className="space-y-2">
            {dlq.items.map((it) => (
              <div key={it.id} data-testid={`dlq-row-${it.id}`} className="border border-[#D4A84A]/15 p-3 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-hud text-xs text-[#D4A84A] tracking-wider uppercase">{it.event}</div>
                  <div className="flex items-center gap-2">
                    {it.resolved
                      ? <span className="text-[10px] font-mono text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 size={10} />resolved</span>
                      : it.discarded
                      ? <span className="text-[10px] font-mono text-white/40 inline-flex items-center gap-1"><Trash2 size={10} />discarded</span>
                      : <span className="text-[10px] font-mono text-[#FF4444] inline-flex items-center gap-1"><AlertTriangle size={10} />open</span>}
                    {!it.resolved && !it.discarded && (
                      <>
                        <button data-testid={`dlq-retry-${it.id}`} onClick={() => onRetry(it.id)}
                          className="font-hud text-[10px] tracking-wider text-[#2F8A92] hover:text-[#D4A84A] inline-flex items-center gap-1">
                          <RotateCcw size={10} /> RETRY
                        </button>
                        <button data-testid={`dlq-discard-${it.id}`} onClick={() => onDiscard(it.id)}
                          className="font-hud text-[10px] tracking-wider text-white/55 hover:text-[#FF4444] inline-flex items-center gap-1">
                          <Trash2 size={10} /> DISCARD
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="font-mono text-[10px] text-white/55">target: {it.target_url}</div>
                <div className="font-mono text-[10px] text-white/55">attempts: {it.attempts} · last: {it.last_attempt_at}</div>
                {it.last_error && <div className="font-mono text-[10px] text-red-300">err: {it.last_error}</div>}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Reference demand */}
      <section className="glass-panel p-4 space-y-3" data-testid="ops-demand-panel">
        <div className="flex items-center gap-2 font-hud text-xs tracking-widest text-[#2F8A92] uppercase">
          <Activity size={14} /> Reference Demand
          <span className="ml-auto font-mono text-[10px] text-white/45">threshold = {demand.threshold} inbound refs</span>
        </div>
        {demand.items.length === 0 ? (
          <div data-testid="demand-empty" className="font-mono text-[11px] text-white/45 italic">No referenced-but-missing chains yet. Closed loop is quiet.</div>
        ) : (
          <div className="space-y-2">
            {demand.items.map((row) => {
              const fired = !!row.threshold_fired_at;
              return (
                <div key={row.id} data-testid={`demand-row-${row.id}`} className="border border-[#D4A84A]/15 p-3 space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="font-hud text-xs text-[#D4A84A] tracking-wider">{row.target_chain_title || '(no title)'}</div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-white/65">refs: {row.demand_count}</span>
                      <span className={`font-mono text-[10px] ${fired ? 'text-emerald-400' : 'text-white/45'}`}>
                        {fired ? '↳ fired' : 'pending'}
                      </span>
                    </div>
                  </div>
                  {row.target_scope_slug && (
                    <div className="font-mono text-[10px] text-white/45">scope: {row.target_scope_slug}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Source link health */}
      <section className="glass-panel p-4 space-y-3" data-testid="ops-source-health-panel">
        <div className="flex flex-wrap items-center gap-2 font-hud text-xs tracking-widest text-[#2F8A92] uppercase">
          <Link2 size={14} /> Source Link Health
          <span className="ml-auto font-mono text-[10px] text-white/55">
            tracked {sourceHealth.summary?.total_tracked ?? 0} · broken {sourceHealth.summary?.broken ?? 0}
            {sourceHealth.summary?.last_run && (
              <> · last scan {sourceHealth.summary.last_run.completed_at?.slice(0, 19)} ({sourceHealth.summary.last_run.duration_ms}ms)</>
            )}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            data-testid="source-health-scan-btn"
            onClick={onScanSources}
            disabled={scanning}
            className="font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#2F8A92] px-4 py-2 hover:bg-[#2F8A92]/85 disabled:opacity-50 inline-flex items-center gap-2"
          >
            <RefreshCcw size={12} /> {scanning ? 'SCANNING...' : 'RUN SCAN NOW'}
          </button>
          <span className="font-mono text-[10px] text-white/45">
            HEAD-checks every source URL on every locked chain. Marks broken after 2 consecutive failures.
          </span>
        </div>
        {sourceHealth.items?.length === 0 ? (
          <div data-testid="source-health-empty" className="font-mono text-[11px] text-white/45 italic">No broken sources detected. The methodology receipts are intact.</div>
        ) : (
          <div className="space-y-2">
            {sourceHealth.items.slice(0, 50).map((it) => (
              <div key={it.id} data-testid={`source-broken-${it.id}`} className="border border-[#FF4444]/25 p-3 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="font-hud text-xs text-[#D4A84A] tracking-wider">
                    {it.chain_title} · <span className="text-white/55">{it.step_key}</span>
                  </div>
                  <div className="font-mono text-[10px] text-[#FF4444]">
                    {it.last_status > 0 ? `HTTP ${it.last_status}` : 'network err'} · failures: {it.consecutive_failures}
                  </div>
                </div>
                <div className="font-mono text-[10px] text-white/55 break-all">{it.url}</div>
                {it.last_error && <div className="font-mono text-[10px] text-red-300">err: {it.last_error}</div>}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Scheduler observability — last 5 source-health scan runs */}
      <section className="glass-panel p-4 space-y-3" data-testid="ops-scheduler-runs-panel">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-hud text-xs tracking-widest text-[#2F8A92] uppercase">
            <Clock size={14} /> Source-health scheduler · last 5 ticks
          </div>
          <button
            onClick={loadRecentRuns}
            disabled={runsLoading}
            data-testid="ops-scheduler-runs-refresh"
            className="font-hud text-[10px] tracking-widest text-white/55 hover:text-[#2F8A92]"
          >
            {runsLoading ? '…' : 'REFRESH'}
          </button>
        </div>
        {recentRuns.length === 0 ? (
          <p className="font-mono text-[11px] text-white/55">
            No scheduled scans recorded yet. Default cadence: 6h interval, 5min initial delay after backend boot. Configurable via SOURCE_HEALTH_SCAN_INTERVAL_HOURS.
          </p>
        ) : (
          <ul className="space-y-1.5" data-testid="ops-scheduler-runs-list">
            {recentRuns.map((r, i) => {
              const at = r.completed_at || r.started_at;
              const dur = r.duration_ms != null ? `${(r.duration_ms / 1000).toFixed(1)}s` : '—';
              const broken = r.broken ?? r.broken_count ?? 0;
              const scanned = r.scanned ?? r.scanned_count ?? 0;
              const healthy = r.healthy ?? r.healthy_count ?? 0;
              return (
                <li
                  key={r.id || i}
                  data-testid={`ops-scheduler-run-${i}`}
                  className="grid grid-cols-12 gap-2 font-mono text-[11px] py-1.5 border-l-2 border-[#2F8A92]/25 pl-2"
                >
                  <div className="col-span-5 text-white/75">{at ? new Date(at).toLocaleString() : '—'}</div>
                  <div className="col-span-2 text-white/70">scanned <span className="text-white">{scanned}</span></div>
                  <div className="col-span-2 text-emerald-300/85">ok <span className="text-emerald-200">{healthy}</span></div>
                  <div className="col-span-2 text-red-300/85">broken <span className="text-red-200">{broken}</span></div>
                  <div className="col-span-1 text-white/55 text-right">{dur}</div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Pre-flight JSON validator — paste a chain payload, validate without ingesting */}
      <section className="glass-panel p-4 space-y-3" data-testid="ops-validate-panel">
        <div className="flex items-center gap-2 font-hud text-xs tracking-widest text-[#2F8A92] uppercase">
          <FileCheck2 size={14} /> Pre-flight Payload Validator
        </div>
        <p className="font-mono text-[11px] text-white/65 leading-relaxed">
          Paste a candidate chain JSON to dry-run the same validator that the AI writer's CLI uses.
          No DB writes, no webhooks, no asset ingestion — pure rules check. Use this before pinging
          <span className="text-[#D4A84A]"> /api/pipeline/ingest</span> so a bad payload never burns a real submission.
        </p>
        <textarea
          data-testid="validate-input"
          value={validateInput}
          onChange={(e) => setValidateInput(e.target.value)}
          placeholder='{"chain_title": "...", "era": "ancient", "scope": "world", "scope_slug": "world", "what_survived": "...", "steps": [...], "cross_references": []}'
          className="w-full min-h-[180px] bg-black/60 border border-[#D4A84A]/20 text-white/85 font-mono text-[11px] p-3 focus:outline-none focus:border-[#D4A84A]/55"
        />
        <div className="flex flex-wrap items-center gap-3">
          <button
            data-testid="validate-btn"
            onClick={onValidatePayload}
            disabled={validating || !validateInput.trim()}
            className="font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#2F8A92] px-4 py-2 hover:bg-[#2F8A92]/85 disabled:opacity-50 inline-flex items-center gap-2"
          >
            <FileCheck2 size={12} /> {validating ? 'VALIDATING...' : 'VALIDATE'}
          </button>
          <button
            data-testid="validate-load-template-btn"
            onClick={onLoadTemplate}
            disabled={loadingTemplate}
            className="font-hud text-xs tracking-widest text-[#D4A84A] border border-[#D4A84A]/40 px-4 py-2 hover:bg-[#D4A84A]/10 disabled:opacity-50 inline-flex items-center gap-2"
          >
            <ClipboardCopy size={12} /> {loadingTemplate ? 'LOADING…' : 'LOAD TEMPLATE'}
          </button>
          {validateInput && (
            <button
              data-testid="validate-clear-btn"
              onClick={() => { setValidateInput(''); setValidateResult(null); setValidateError(null); }}
              className="font-hud text-[10px] tracking-widest text-white/55 hover:text-[#D4A84A]"
            >
              CLEAR
            </button>
          )}
        </div>

        {validateError && (
          <div data-testid="validate-error-block" className="font-mono text-[11px] text-red-300 border-l-2 border-red-500 pl-3 py-1">
            {validateError}
          </div>
        )}

        {validateResult && (
          <div data-testid="validate-result-block" className="border border-[#D4A84A]/15 p-3 space-y-2">
            <div className="flex items-center gap-3">
              <span
                data-testid="validate-status-badge"
                className={`font-hud text-[10px] tracking-widest px-2 py-1 ${
                  validateResult.ok
                    ? 'text-emerald-300 border border-emerald-400/40 bg-emerald-400/5'
                    : 'text-red-300 border border-red-500/40 bg-red-500/5'
                }`}
              >
                {validateResult.ok ? 'VALID' : 'INVALID'}
              </span>
              <span className="font-mono text-[10px] text-white/55">
                errors {validateResult.summary?.error_count ?? 0} ·
                warnings {validateResult.summary?.warning_count ?? 0} ·
                notes {validateResult.summary?.note_count ?? 0}
              </span>
            </div>

            {validateResult.parsed_summary && Object.keys(validateResult.parsed_summary).length > 0 && (
              <div data-testid="validate-parsed-summary" className="font-mono text-[10px] text-white/65 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
                {validateResult.parsed_summary.chain_title && <div>title: <span className="text-[#D4A84A]">{validateResult.parsed_summary.chain_title}</span></div>}
                {validateResult.parsed_summary.era && <div>era: <span className="text-white/85">{validateResult.parsed_summary.era}</span></div>}
                {validateResult.parsed_summary.scope_slug && <div>scope: <span className="text-white/85">{validateResult.parsed_summary.scope_slug}</span></div>}
                <div>steps: <span className="text-white/85">{validateResult.parsed_summary.step_count ?? 0}</span></div>
                <div>sources: <span className="text-white/85">{validateResult.parsed_summary.source_count ?? 0}</span></div>
                <div>inline cites: <span className="text-white/85">{validateResult.parsed_summary.inline_citation_count ?? 0}</span></div>
                <div>cross-refs: <span className="text-white/85">{validateResult.parsed_summary.cross_reference_count ?? 0}</span></div>
              </div>
            )}

            {validateResult.errors?.length > 0 && (
              <div data-testid="validate-errors-list" className="space-y-1">
                <div className="font-hud text-[10px] tracking-widest text-red-400 uppercase">{validateResult.errors.length} error(s)</div>
                {validateResult.errors.map((e, i) => (
                  <div key={i} className="font-mono text-[10px] text-red-300 border-l border-red-500/40 pl-2">x {e}</div>
                ))}
              </div>
            )}

            {validateResult.warnings?.length > 0 && (
              <div data-testid="validate-warnings-list" className="space-y-1">
                <div className="font-hud text-[10px] tracking-widest text-amber-300 uppercase">{validateResult.warnings.length} warning(s)</div>
                {validateResult.warnings.map((w, i) => (
                  <div key={i} className="font-mono text-[10px] text-amber-200 border-l border-amber-400/40 pl-2">! {w}</div>
                ))}
              </div>
            )}

            {validateResult.notes?.length > 0 && (
              <div data-testid="validate-notes-list" className="space-y-1">
                <div className="font-hud text-[10px] tracking-widest text-white/55 uppercase">{validateResult.notes.length} note(s)</div>
                {validateResult.notes.map((n, i) => (
                  <div key={i} className="font-mono text-[10px] text-white/65 border-l border-white/20 pl-2">. {n}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* Demo trigger — one-click pipeline walkthrough for judges */}
      <section className="glass-panel p-4 space-y-3" data-testid="ops-demo-trigger-panel">
        <div className="flex items-center gap-2 font-hud text-xs tracking-widest text-[#2F8A92] uppercase">
          <Zap size={14} /> Demo Trigger
        </div>
        <p className="font-mono text-[11px] text-white/65 leading-relaxed">
          One-click ingest of a fixture chain in DRAFT state. Use this to walk a judge through the full
          orchestrator loop — draft → review → lock → spine activation → outbound webhook — without Genspark/Claw needing to be live.
          Idempotent on the fixture title; re-running returns the existing chain.
        </p>
        <button
          data-testid="trigger-sample-chain-btn"
          onClick={onTriggerSample}
          disabled={triggering}
          className="font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-4 py-2 hover:bg-[#D4A84A]/85 disabled:opacity-50 inline-flex items-center gap-2"
        >
          <Zap size={12} /> {triggering ? 'INGESTING...' : 'TRIGGER SAMPLE CHAIN'}
        </button>
      </section>

      {/* API key */}
      <section className="glass-panel p-4 space-y-3" data-testid="ops-api-key-panel">
        <div className="flex items-center gap-2 font-hud text-xs tracking-widest text-[#2F8A92] uppercase">
          <KeyRound size={14} /> Pipeline API Key
        </div>
        <div className="font-mono text-[11px] text-white/65 space-y-1">
          <div>configured: <span className={keyStatus?.configured ? 'text-emerald-300' : 'text-[#FF4444]'}>{String(keyStatus?.configured)}</span></div>
          <div>fingerprint: <span className="text-[#D4A84A] break-all">{keyStatus?.fingerprint || '—'}</span></div>
          <div>rotated_at: {keyStatus?.rotated_at || '(never rotated since last deploy)'}</div>
          {keyStatus?.rotated_by && <div>rotated_by: {keyStatus.rotated_by}</div>}
        </div>
        {revealedKey && (
          <div data-testid="rotated-key-reveal" className="border border-emerald-400/40 bg-emerald-400/5 p-3 space-y-2">
            <div className="font-hud text-[10px] tracking-widest text-emerald-300">NEW KEY — STORE NOW. SHOWN ONCE.</div>
            <div className="font-mono text-[11px] text-emerald-200 break-all select-all">{revealedKey}</div>
            <button
              data-testid="copy-rotated-key-btn"
              onClick={() => COPY_FALLBACK(revealedKey)}
              className="font-hud text-[10px] tracking-widest text-[#2F8A92] hover:text-[#D4A84A] inline-flex items-center gap-1"
            >
              <Copy size={10} /> COPY
            </button>
          </div>
        )}
        <div className="flex items-center gap-3">
          <button
            data-testid="rotate-api-key-btn"
            disabled={rotating}
            onClick={onRotate}
            className="font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-4 py-2 hover:bg-[#D4A84A]/85 disabled:opacity-50 inline-flex items-center gap-2"
          >
            <RotateCcw size={12} /> {rotating ? 'ROTATING...' : 'ROTATE KEY'}
          </button>
          <span className="font-mono text-[10px] text-white/45">
            Old key stops working immediately. Update the orchestrator config before next ingest.
          </span>
        </div>
      </section>
    </div>
  );
}
